import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, makeStyles, Paper } from '@material-ui/core';
import { Block, Heading, Typography } from '@klp/design';
import { NumberFormatted } from '@klp/formgenerator/components/NumberFormatted';
import type { ProsjektAvfall } from '../services/rtkApi';

const useStyles = makeStyles((theme) => ({
    paper: {
        height: '100%',
        '& > div': {
            height: '100%',
        },
        marginBottom: theme.spacing(2),
    },
    itemText: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    valueText: {
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
}));

type ProsjektAvfallSummaryProps = {
    prosjektAvfall: Array<ProsjektAvfall>;
};

export function ProsjektAvfallSummary({
    prosjektAvfall,
}: ProsjektAvfallSummaryProps) {
    const { t } = useTranslation();
    const classes = useStyles();

    const totalSum = useMemo(
        () =>
            prosjektAvfall.reduce(
                (partialsum, pa) => partialsum + pa.totalMengde,
                0,
            ),
        [prosjektAvfall],
    );

    const totalSorted = useMemo(
        () =>
            prosjektAvfall.reduce(
                (partialsum, pa) => partialsum + pa.totalMengdeSortert,
                0,
            ),
        [prosjektAvfall],
    );

    const sorteringsgrad = useMemo(
        () => (totalSum > 0 ? (totalSorted / totalSum) * 100 : 0),
        [totalSum, totalSorted],
    );

    return (
        <Grid container={true} spacing={3}>
            <Grid item={true} xs={12} md={6}>
                <Paper className={classes.paper}>
                    <Block padding="compact">
                        <Block marginBottom="compact">
                            <Heading
                                klpHeadingLevel="THREE"
                                klpColor="dark"
                                translation="sustainability.prosjektAvfall.summary"
                            />
                        </Block>
                        <Typography className={classes.itemText}>
                            {t(
                                'sustainability.prosjektAvfall.antallProsjekter',
                            )}
                            :
                            <NumberFormatted
                                className={classes.valueText}
                                value={prosjektAvfall.length}
                            />
                        </Typography>
                        <Typography className={classes.itemText}>
                            {t(
                                'sustainability.prosjektAvfall.antallProsjekterMedRapport',
                            )}
                            :
                            <NumberFormatted
                                className={classes.valueText}
                                value={
                                    prosjektAvfall.filter(
                                        (p) => p.avfallDokumentId !== null,
                                    ).length
                                }
                            />
                        </Typography>
                        <Typography className={classes.itemText}>
                            {t('sustainability.prosjektAvfall.sorteringsgrad')}
                            :
                            <NumberFormatted
                                className={classes.valueText}
                                value={
                                    sorteringsgrad > 0
                                        ? sorteringsgrad.toFixed(1)
                                        : sorteringsgrad
                                }
                                suffix=" %"
                            />
                        </Typography>
                        <Typography className={classes.itemText}>
                            {t('sustainability.prosjektAvfall.totalAvfall')}
                            :
                            <NumberFormatted
                                className={classes.valueText}
                                value={totalSum}
                                suffix=" kg"
                            />
                        </Typography>
                    </Block>
                </Paper>
            </Grid>
        </Grid>
    );
}
